import { Collapse, useMediaQuery } from "@mui/material";
import moment from "moment";
import { useRouter } from "next/router";
import React, { Fragment, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { localisation } from "../../../helpers/constant";
import { getLocaleCopies } from "../../../helpers/locale_management";
import { sendTrackingEvent } from "../../../helpers/tracking_management";
import {
  clearFilters,
  getJobs,
  updateFilterApplied,
  updateJobsParams,
  updateLoadJobKey,
} from "../../../redux/actions/job_action";
import {
  AutocompleteStyled,
  ButtonStyled,
  CheckBoxIconStyled,
  CheckBoxOutlineBlankIconStyled,
  CheckBoxStyled,
  ChipStyled,
  ChipWrapper,
  EmptyContainer,
  FilterButtonStyled,
  FormControlSalaryStyled,
  FormControlStyled,
  IconButtonStyled,
  InnerSalaryStyled,
  InputAdornmentStyled,
  OutlinedInputStyled,
  RadioGroupStyled,
  RadioLabelStyled,
  RadioStyled,
  SearchIconStyled,
  TextFieldSalaryStyled,
  TextFieldStyled,
  Wrapper,
} from "./styles";

const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      func(...args);
    }, delay);
  };
};

const SingleFilter = (props) => {
  const { type, options } = props;
  const dispatch = useDispatch();
  const router = useRouter();
  const fetchingAllJobs = useSelector((state) => state.jobs.fetchingAllJobs);
  const jobParams = useSelector((state) => state.jobs.jobsParams);
  const icon = <CheckBoxOutlineBlankIconStyled fontSize="small" />;
  const checkedIcon = <CheckBoxIconStyled fontSize="small" />;

  const copies = getLocaleCopies();

  let filterPlaceholder;

  let newFilter;

  newFilter = {
    ...jobParams,
  };

  useEffect(() => {
    newFilter = {
      ...jobParams,
    };
  }, [jobParams]);

  let data = [];
  let targetObject;
  let targetObjectFull;

  if (type === "Specialization") {
    filterPlaceholder = copies.specialisationFilter;
    targetObject = "trackIds";
    targetObjectFull = "trackIdsFull";
    data = options.map((item) => {
      const labelName =
        router.locale === localisation.BAHASA_MALAYSIA
          ? item.titleBm
          : item.title;

      return {
        label: labelName,
        labelEng: item?.title,
        labelBm: item?.titleBm,
        id: item.id,
      };
    });
  } else if (type === "States") {
    filterPlaceholder = copies.statesFilter;
    targetObject = "stateRegions";
    targetObjectFull = "stateRegionsFull";
    data = options.map((item) => {
      return {
        label: item.state,
        id: item.id,
      };
    });
  } else if (type === "Job Types") {
    filterPlaceholder = copies.jobTypeFilter;
    targetObject = "jobTypeIds";
    targetObjectFull = "jobTypeIdsFull";
    data = options.map((item) => {
      const labelName =
        router.locale === localisation.BAHASA_MALAYSIA
          ? item.titleBm
          : item.title;

      return {
        label: labelName,
        labelEng: item?.title,
        labelBm: item?.titleBm,
        id: item.id,
      };
    });
  } else if (type === "Experience Level") {
    filterPlaceholder = copies.experienceLevelFilter;
    targetObject = "experienceIds";
    targetObjectFull = "experienceIdsFull";
    data = options.map((item) => {
      const labelName =
        router.locale === localisation.BAHASA_MALAYSIA
          ? item.titleBm
          : item.title;

      return {
        label: labelName,
        labelEng: item?.title,
        labelBm: item?.titleBm,
        id: item.id,
      };
    });
  } else if (type === "Salary") {
    filterPlaceholder = copies.salaryFilter;
  }

  // When user closes the autocomplete menu, update the jobFilter in redux
  const activate = useCallback(() => {
    dispatch(updateJobsParams(newFilter));
  }, [dispatch, newFilter]);

  // When user select or unselect any of the options in the autocomplete
  // update the jobFilter in redux as well
  // the useCallback is to prevent the function from re-rendering without any changes
  const handleFilterChange = useCallback(
    (event, value) => {
      let id;
      if (type === "States") {
        id = value.map((item) => item.label);
      } else {
        id = value.map((item) => parseInt(item.id));
      }
      newFilter = {
        ...newFilter,
        [targetObject]: id,
        [targetObjectFull]: value,
      };
      dispatch(updateJobsParams(newFilter));
    },
    [newFilter, targetObject, type]
  );

  const selected = newFilter[targetObjectFull]?.length;

  return (
    <AutocompleteStyled
      disabled={fetchingAllJobs}
      multiple
      key={type}
      value={newFilter[targetObjectFull]}
      isOptionEqualToValue={(option, value) =>
        option.id === value.id || option.label === value.label
      }
      id="checkboxes-tags-demo"
      options={data}
      onClose={activate}
      onChange={handleFilterChange}
      disableCloseOnSelect
      getOptionLabel={(option) => {
        if (option) {
          return option.label;
        }
        return "";
      }}
      renderOption={(props, option, { selected }) => (
        <li {...props}>
          <CheckBoxStyled
            key={option.id}
            icon={icon}
            checkedIcon={checkedIcon}
            style={{ marginRight: 8 }}
            checked={selected}
          />
          {option.label}
        </li>
      )}
      renderTags={(value, getTagProps) => null}
      renderInput={(params) => (
        <TextFieldStyled
          {...params}
          placeholder={
            selected > 0
              ? `${filterPlaceholder} - ${selected}`
              : filterPlaceholder
          }
          key={type}
        />
      )}
    />
  );
};

const SalaryFilterMenu = () => {
  const router = useRouter();
  const dispatch = useDispatch();
  const [invalidSalary, setInvalidSalary] = useState(false);
  const [rate, setRate] = useState(0);
  const [type, setType] = useState(""); // salary type in ID
  const [salaryType, setSalaryType] = useState(""); // salary type in full title format (e.g. monthly, weekly)
  const [salaryTypeBm, setSalaryTypeBm] = useState("");
  const fetchingAllJobs = useSelector((state) => state.jobs.fetchingAllJobs);
  const jobParams = useSelector((state) => state.jobs.jobsParams);
  const rateType = useSelector((state) => state.jobs.jobsParams.type);
  const expectedSalary = useSelector(
    (state) => state.jobs.jobsParams.expectedSalary
  );
  const allSalaryTypes = useSelector((state) => state.jobs.salaryTypes);
  const pagination = useSelector((state) => state.jobs.pagination);

  const copies = getLocaleCopies();

  let newFilter;
  newFilter = {
    ...jobParams,
  };

  const debouncedGetJobs = useCallback(
    debounce((params, pagination) => {
      dispatch(getJobs(params, pagination));
      // dispatch(updateFilterApplied(true));
    }, 1000),
    []
  );

  useEffect(() => {
    const loadJobKey = moment().format("dd/mm/yyyy hh:mm:ss");
    dispatch(updateLoadJobKey(loadJobKey)).then(() => {
      const params = {
        ...jobParams,
        loadJobKey: loadJobKey,
      };

      debouncedGetJobs(params, pagination);
    });
  }, [
    jobParams.expectedSalary,
    jobParams.type,
    debouncedGetJobs,
    dispatch,
    pagination,
  ]);

  const textHandler = (e) => {
    if (e.target.value >= 0 && e.target.value.toString().charAt(0) !== "0") {
      setInvalidSalary(false);
      newFilter = {
        ...newFilter,
        expectedSalary: e.target.value,
      };

      setRate(e.target.value);
      dispatch(updateJobsParams(newFilter));
    } else {
      setInvalidSalary(true);
    }
  };

  const radioHandler = (props) => {
    const { id, labelEng, labelBm } = props;

    setType(id);
    setSalaryType(labelEng);
    setSalaryTypeBm(labelBm);
  };

  useEffect(() => {
    let salary;
    let salaryBm;

    if (rate < 1) {
      salary = salaryType;
      salaryBm = salaryTypeBm;
    } else {
      salary = "RM" + rate + "/" + salaryType;
      salaryBm = "RM" + rate + "/" + salaryTypeBm;
    }

    newFilter = {
      ...newFilter,
      type: type,
      expectedSalary: parseInt(rate),
      expectedSalaryFull: salary,
      expectedSalaryFullBm: salaryBm,
    };

    if (rate > 0 || type !== null || rate !== "") {
      dispatch(updateJobsParams(newFilter));
    }
  }, [rate, type]);

  return (
    <Fragment>
      <FormControlSalaryStyled placeholder={expectedSalary}>
        <TextFieldSalaryStyled
          placeholder={copies.salaryFilter}
          // onChange={applyFilter}
          label={
            expectedSalary > 0 ? `RM ${expectedSalary}` : copies.salaryFilter
          }
          select
          disabled={fetchingAllJobs}
        >
          <EmptyContainer
            container
            direction="column"
            width="100%"
            height="100%"
            padding="5%"
          >
            <InnerSalaryStyled
              min={0}
              error={invalidSalary}
              helperText={invalidSalary ? "Enter valid salary" : ""}
              onChange={textHandler}
              value={expectedSalary}
              type="number"
              placeholder="Enter rate"
              InputProps={{
                startAdornment: (
                  <InputAdornmentStyled position="start">
                    RM
                  </InputAdornmentStyled>
                ),
              }}
            />

            <FormControlSalaryStyled required>
              <RadioGroupStyled defaultValue={rateType} value={rateType}>
                {Array.isArray(allSalaryTypes) &&
                  allSalaryTypes.map((salaryType) => {
                    // based on current locale, display the different title
                    const labelName =
                      router.locale === localisation.BAHASA_MALAYSIA
                        ? salaryType.titleBm
                        : salaryType.title;

                    return (
                      <RadioLabelStyled
                        onClick={() =>
                          radioHandler({
                            id: salaryType.id,
                            labelEng: salaryType.title,
                            labelBm: salaryType.titleBm,
                          })
                        }
                        value={salaryType.id}
                        control={<RadioStyled />}
                        label={labelName}
                      />
                    );
                  })}
              </RadioGroupStyled>
            </FormControlSalaryStyled>
          </EmptyContainer>
        </TextFieldSalaryStyled>
      </FormControlSalaryStyled>
    </Fragment>
  );
};

const FilterWrapper = () => {
  const [checked, setChecked] = useState(false);
  const dispatch = useDispatch();
  const router = useRouter();
  const tabletView = useMediaQuery("(max-width:1280px)");
  const [clear, setClear] = useState(false);
  const [deletedChip, setDeletedChip] = useState(false);
  const specialisations = useSelector((state) => state.jobs.tracks);
  const stateRegions = useSelector((state) => state.jobs.stateRegions);
  const jobTypes = useSelector((state) => state.jobs.jobTypes);
  const experienceLevels = useSelector((state) => state.jobs.experienceLevels);
  const jobParams = useSelector((state) => state.jobs.jobsParams);
  const pagination = useSelector((state) => state.jobs.pagination);
  const fetchingAllJobs = useSelector((state) => state.jobs.fetchingAllJobs);
  const filterApplied = useSelector((state) => state.jobs.filterApplied);
  const rateType = useSelector((state) => state.jobs.jobsParams.type);
  const expectedSalary = useSelector(
    (state) => state.jobs.jobsParams.expectedSalary
  );

  let params;
  let cloneJobParams = jobParams;
  let searchParams = jobParams;

  useEffect(() => {
    if (tabletView) {
      setChecked(false);
    } else {
      setChecked(true);
    }
  }, [tabletView]);

  // Whenever a user clear search, getJobs API will be called again
  // For some reason, putting the dispatch function inside the clearFilter does not work
  useEffect(() => {
    if (clear === true) {
      const loadJobKey = moment().format("dd/mm/yyyy hh:mm:ss");
      dispatch(updateLoadJobKey(loadJobKey)).then(() => {
        const params = {
          ...jobParams,
          loadJobKey: loadJobKey,
        };

        dispatch(getJobs(params, pagination));
        setClear(false);
      });
    }
  }, [clear, getJobs]);

  // Whenever a user deletes a chip, getJobs API will be called again
  // For some reason, putting the dispatch function inside the removeFilter does not work
  useEffect(() => {
    if (deletedChip === true) {
      const loadJobKey = moment().format("dd/mm/yyyy hh:mm:ss");
      dispatch(updateLoadJobKey(loadJobKey)).then(() => {
        const params = {
          ...jobParams,
          loadJobKey: loadJobKey,
        };

        dispatch(getJobs(params, pagination));
        setDeletedChip(false);
      });
    }
  }, [deletedChip, getJobs]);

  // Whenever user change salary to an invalid value, the chip will be deleted automatically
  useEffect(() => {
    if (
      // expectedSalary == null ||
      // expectedSalary == NaN ||
      rateType === null ||
      rateType === ""
    ) {
      selectedFilter.pop();
    }
  }, [jobParams]);

  // Need to get the label of the selected jobFilter for chip display
  const selectedFilter = [];
  const selectedFilterBm = [];

  // For specialization
  cloneJobParams["trackIdsFull"]?.map((jobFilter) => {
    selectedFilter.push(jobFilter.labelEng);
    selectedFilterBm.push(jobFilter.labelBm);
  });

  // For states and regions
  cloneJobParams["stateRegionsFull"]?.map((jobFilter) => {
    selectedFilter.push(jobFilter.label);
    selectedFilterBm.push(jobFilter.label);
  });

  // For job types
  cloneJobParams["jobTypeIdsFull"]?.map((jobFilter) => {
    selectedFilter.push(jobFilter.labelEng);
    selectedFilterBm.push(jobFilter.labelBm);
  });

  // For experience
  cloneJobParams["experienceIdsFull"]?.map((jobFilter) => {
    selectedFilter.push(jobFilter.labelEng);
    selectedFilterBm.push(jobFilter.labelBm);
  });

  // For salary
  if (
    rateType !== "" &&
    rateType !== null &&
    cloneJobParams["experienceIdsFull"].includes("null") === false
  ) {
    selectedFilter.push(cloneJobParams["expectedSalaryFull"]);
    selectedFilterBm.push(cloneJobParams["expectedSalaryFullBm"]);
  }

  // When user delete one of the filter by clicking X on the chip, the jobFilter in redux needs to be updated
  // This function will receive the deleted chip label
  // Find the id of the label by referring back to the specialisation array
  // Use that id to update the jobFilter in redux
  const removeFilter = (value) => {
    let filterTarget = "trackIds";
    let filterTargetFull = "trackIdsFull";
    let target;
    let id;

    // check where is the deleted item coming from
    // because we do not know so we need to go through every filter object to check
    if (target === undefined) {
      target = specialisations.find(
        (spec) => spec.title === value || spec.titleBm === value
      );
    }

    if (target === undefined) {
      target = stateRegions.find((state) => {
        return state.state === value;
      });
      filterTarget = "stateRegions";
      filterTargetFull = "stateRegionsFull";
    }

    if (target === undefined) {
      target = jobTypes.find(
        (type) => type.title === value || type.titleBm === value
      );
      filterTarget = "jobTypeIds";
      filterTargetFull = "jobTypeIdsFull";
    }

    if (target === undefined) {
      target = experienceLevels.find(
        (exp) => exp.title === value || exp.titleBm === value
      );
      filterTarget = "experienceIds";
      filterTargetFull = "experienceIdsFull";
    }

    // Because we need to get the label for states and id for the others
    if (filterTargetFull === "stateRegionsFull") {
      id = target?.state;
    } else {
      id = target?.id;
    }

    params = {
      ...jobParams,
    };

    // This if else statement is used because the state uses string as filter input
    // The rest of the filter uses id
    if (filterTargetFull === "stateRegionsFull") {
      params[filterTarget] = params[filterTarget].filter((item) => item !== id);
      params[filterTargetFull] = params[filterTargetFull].filter(
        (item) => item.label !== id
      );

      dispatch(updateJobsParams(params));
    } else if (target === undefined) {
      // Salary filter only has one and will always be appended to the end of the array
      // Just remove last element when want to remove from job filter
      params = {
        ...jobParams,
        expectedSalary: 0,
        type: null,
        expectedSalaryFull: "",
      };

      dispatch(updateJobsParams(params));
    } else {
      const index = params[filterTarget].indexOf(parseInt(id));
      if (index > -1) {
        params[filterTarget].splice(index, 1);
        params[filterTargetFull].splice(index, 1);
      }

      dispatch(updateJobsParams(params));
    }

    setDeletedChip(true);
    triggerFilterClickEvent(params);
  };

  const showFilters = () => {
    setChecked(!checked);
  };

  const applyFilter = () => {
    const loadJobKey = moment().format("dd/mm/yyyy hh:mm:ss");
    dispatch(updateLoadJobKey(loadJobKey)).then(() => {
      const params = {
        ...jobParams,
        loadJobKey: loadJobKey,
      };

      dispatch(getJobs(params, pagination));
    });

    dispatch(clearFilters());
    dispatch(updateFilterApplied(true));
    triggerFilterClickEvent(jobParams);
    triggerSearchClickEvent(jobParams?.keyword);
  };

  const clearFilter = (e) => {
    const clearParams = {
      expectedSalary: 0,
      type: null,
      expectedSalaryFull: "",
      expectedSalaryFullBm: "",
      keyword: "*",
      keywordDisplay: "",
      trackIds: [],
      trackIdsFull: [],
      stateRegions: [],
      stateRegionsFull: [],
      jobTypeIds: [],
      jobTypeIdsFull: [],
      experienceIds: [],
      experienceIdsFull: [],
    };

    dispatch(updateJobsParams(clearParams));
    dispatch(updateFilterApplied(false));
    dispatch(clearFilters());
    setClear(true);
  };

  const searchHandler = (e) => {
    searchParams = {
      ...searchParams,
      keyword: e.target.value,
      keywordDisplay: e.target.value,
    };

    if (searchParams.keyword.length === 0) {
      searchParams = {
        ...searchParams,
        keyword: "*",
      };
    }
    dispatch(updateJobsParams(searchParams));
  };

  const searchClick = () => {
    const loadJobKey = moment().format("dd/mm/yyyy hh:mm:ss");
    dispatch(updateLoadJobKey(loadJobKey)).then(() => {
      const params = {
        ...jobParams,
        loadJobKey: loadJobKey,
      };
      dispatch(getJobs(params, pagination));
    });
    dispatch(updateFilterApplied(true));
    triggerSearchClickEvent(searchParams?.keyword);
  };

  const onKeyDown = (event) => {
    if (event.key === "Enter") {
      searchClick();
    }
  };

  const copies = getLocaleCopies();

  const triggerSearchClickEvent = (searchTerm) => {
    sendTrackingEvent({
      event: "CE_search-job",
      "search-term": searchTerm,
    });
  };
  const triggerFilterClickEvent = (filterParams) => {
    let minExpParams, maxExpParams;
    const minExp = Math.min(...filterParams?.experienceIds);
    const maxExp = Math.max(...filterParams?.experienceIds);

    minExpParams =
      minExp === 1 || minExp === 2
        ? 0
        : minExp === 3
        ? 1
        : minExp === 4
        ? 4
        : minExp === 5
        ? 8
        : minExp === 6
        ? 10
        : "";

    maxExpParams =
      maxExp === 1 || maxExp === 2
        ? 0
        : maxExp === 3
        ? 3
        : maxExp === 4
        ? 7
        : maxExp === 5
        ? 10
        : maxExp === 6
        ? 10
        : "";

    sendTrackingEvent({
      event: "CE_apply-filter-job-list",
      "search-term": filterParams?.keyword,
      specialization: filterParams?.trackIdsFull?.map((item) => item.labelEng),
      state: filterParams?.stateRegions,
      "job-type": filterParams?.jobTypeIdsFull?.map((item) => item.labelEng),
      "min-exp": minExpParams,
      "max-exp": maxExpParams,
      salary: filterParams?.expectedSalary,
      "salary-type": filterParams?.type,
    });
  };

  return (
    <Wrapper
      container
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      sx={{
        marginTop: 0,
        marginBottom: 0,
      }}
    >
      <EmptyContainer
        container
        alignItems="center"
        justifyContent={{ xs: "space-between", tablet: "center" }}
        gap={tabletView ? "8px" : "15px"}
        paddingX="1%"
        width={{ xs: "98%", tablet: "60%" }}
      >
        <FormControlStyled variant="outlined">
          <OutlinedInputStyled
            disabled={fetchingAllJobs}
            onChange={searchHandler}
            onKeyDown={onKeyDown}
            value={jobParams.keywordDisplay}
            placeholder={copies.searchAllFields}
            endAdornment={
              <InputAdornmentStyled position="end">
                <IconButtonStyled edge="end" onClick={searchClick}>
                  <SearchIconStyled />
                </IconButtonStyled>
              </InputAdornmentStyled>
            }
          />
        </FormControlStyled>

        {tabletView && (
          <ButtonStyled onClick={showFilters} variant="contained">
            {checked ? copies.hideFilters : copies.showFilters}
          </ButtonStyled>
        )}
      </EmptyContainer>

      {selectedFilter.length > 0 && tabletView && !checked && filterApplied ? (
        <EmptyContainer
          container
          direction="row"
          maxHeight="36px"
          paddingX="1%"
          width={{ xs: "98%", tablet: "60%" }}
          paddingTop={tabletView ? "15px" : ""}
          marginBottom={tabletView ? "15px" : ""}
        >
          <ChipWrapper>
            {router.locale === localisation.BAHASA_MALAYSIA
              ? selectedFilterBm.map((filter) => {
                  return (
                    <ChipStyled
                      key={filter}
                      label={filter}
                      variant="outlined"
                      onDelete={() => {
                        removeFilter(filter);
                      }}
                    />
                  );
                })
              : selectedFilter.map((filter) => {
                  return (
                    <ChipStyled
                      key={filter}
                      label={filter}
                      variant="outlined"
                      onDelete={() => {
                        removeFilter(filter);
                      }}
                    />
                  );
                })}
          </ChipWrapper>
        </EmptyContainer>
      ) : null}

      <Collapse in={checked} collapsedSize={0} sx={{ width: "100%" }}>
        <EmptyContainer
          container
          width="100%"
          justifyContent={tabletView ? "center" : "space-between"}
          gap="15px"
          height="auto"
          maxWidth={{ xs: "800px", lg: "1300px" }}
          margin="auto"
          alignItems="center"
          paddingTop="15px"
        >
          <SingleFilter type="Specialization" options={specialisations} />
          <SingleFilter type="States" options={stateRegions} />
          <SingleFilter type="Job Types" options={jobTypes} />
          <SingleFilter type="Experience Level" options={experienceLevels} />
          <SalaryFilterMenu />
          <EmptyContainer
            container
            gap="20px"
            alignItems="center"
            flexDirection="row"
            maxHeight="50px"
            flexWrap="nowrap"
            justifyContent={
              selectedFilter.length < 1 || !filterApplied || tabletView
                ? "center"
                : "space-between"
            }
          >
            {selectedFilter.length > 0 && !tabletView && filterApplied ? (
              <ChipWrapper>
                {router.locale === localisation.BAHASA_MALAYSIA
                  ? selectedFilterBm.map((filter) => {
                      return (
                        <ChipStyled
                          key={filter}
                          label={filter}
                          variant="outlined"
                          onDelete={() => {
                            removeFilter(filter);
                          }}
                        />
                      );
                    })
                  : selectedFilter.map((filter) => {
                      return (
                        <ChipStyled
                          key={filter}
                          label={filter}
                          variant="outlined"
                          onDelete={() => {
                            removeFilter(filter);
                          }}
                        />
                      );
                    })}
              </ChipWrapper>
            ) : null}

            <EmptyContainer
              container
              justifyContent={"center"}
              direction="row"
              sx={{ maxWidth: { xs: "100%", lg: "380px" }, gap: "20px" }}
            >
              <FilterButtonStyled
                variant="outlined"
                onClick={clearFilter}
                disabled={fetchingAllJobs}
              >
                {copies.clearFilter}
              </FilterButtonStyled>
              <FilterButtonStyled
                solid={true}
                variant="contained"
                onClick={applyFilter}
                disabled={fetchingAllJobs}
              >
                {copies.applyFilter}
              </FilterButtonStyled>
            </EmptyContainer>
          </EmptyContainer>
        </EmptyContainer>
      </Collapse>
    </Wrapper>
  );
};

export default FilterWrapper;
